import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, query } from "../../lib/api";
import { formatDateTime } from "../../lib/format";
import type { BlocklistEntry, FraudDecision, ListResponse, VelocityRule } from "../../lib/types";
import { Checkbox, toMinor } from "./PaymentLinks";
import {
  Button,
  EmptyState,
  ErrorNotice,
  Field,
  Modal,
  PageHeader,
  Sheet,
  SheetHeader,
  Skeleton,
  StatusPill,
  Table,
  Td,
  Th,
  Tr,
} from "../../components/ui";

const KIND_LABELS: Record<string, string> = {
  mobile: "Mobile number",
  email: "Email address",
  ip: "IP address",
  card_fingerprint: "Card",
};

/**
 * FraudControls is deliberately small.
 *
 * The rails here give no card network signals, no device fingerprint and no issuer risk
 * score, so the honest thing to offer is two controls a merchant can read, understand,
 * and explain to a customer they have just refused: a blocklist, and a cap on how much
 * one payer may transact in a window. The decisions list below is what turns a refusal
 * into an answer.
 */
export function FraudControls() {
  const queryClient = useQueryClient();
  const [blocking, setBlocking] = useState(false);
  const [addingRule, setAddingRule] = useState(false);

  const blocklist = useQuery({
    queryKey: ["blocklist"],
    queryFn: () => api.get<ListResponse<BlocklistEntry>>("/api/dashboard/fraud/blocklist?limit=100"),
  });

  const rules = useQuery({
    queryKey: ["velocity-rules"],
    queryFn: () => api.get<ListResponse<VelocityRule>>("/api/dashboard/fraud/velocity-rules"),
  });

  const decisions = useQuery({
    queryKey: ["fraud-decisions"],
    queryFn: () =>
      api.get<ListResponse<FraudDecision>>(`/api/dashboard/fraud/decisions${query({ limit: 25 })}`),
  });

  const unblock = useMutation({
    mutationFn: (id: string) => api.del<void>(`/api/dashboard/fraud/blocklist/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["blocklist"] }),
  });

  const toggleRule = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.patch<void>(`/api/dashboard/fraud/velocity-rules/${id}`, { enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["velocity-rules"] }),
  });

  const deleteRule = useMutation({
    mutationFn: (id: string) => api.del<void>(`/api/dashboard/fraud/velocity-rules/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["velocity-rules"] }),
  });

  const entries = blocklist.data?.data ?? [];
  const ruleRows = rules.data?.data ?? [];
  const decisionRows = decisions.data?.data ?? [];

  return (
    <>
      <PageHeader
        eyebrow="Account"
        title="Fraud controls"
        description="Two controls, both yours to set: values you will never accept, and limits on how fast one payer may transact."
      />

      <div className="grid gap-6">
        <Sheet className="settle settle-1 overflow-hidden">
          <SheetHeader
            title="Blocklist"
            description="Payments from these are refused before they reach the gateway."
            actions={<Button onClick={() => setBlocking(true)}>Block a value</Button>}
          />
          {blocklist.isLoading ? (
            <Skeleton rows={3} />
          ) : blocklist.error ? (
            <ErrorNotice error={blocklist.error} className="m-5" />
          ) : entries.length === 0 ? (
            <EmptyState
              title="Nothing is blocked"
              description="Block a number or an address after a payment you know was fraudulent."
              action={<Button onClick={() => setBlocking(true)}>Block a value</Button>}
            />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Type</Th>
                  <Th>Value</Th>
                  <Th>Reason</Th>
                  <Th>Set by</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <Tr key={entry.id}>
                    <Td className="text-ink-muted">{KIND_LABELS[entry.kind] ?? entry.kind}</Td>
                    <Td>
                      <code className="font-mono text-[12.5px] text-ink-strong">{entry.value}</code>
                    </Td>
                    <Td className="text-ink-muted">{entry.reason || "—"}</Td>
                    <Td>
                      {entry.scope === "platform" ? (
                        <span className="text-[12.5px] text-ink-muted">Bushveld</span>
                      ) : (
                        <span className="text-[12.5px] text-ink-muted">{entry.created_by || "You"}</span>
                      )}
                    </Td>
                    <Td align="right">
                      {entry.scope === "platform" ? (
                        <span className="text-[12px] text-ink-faint">Platform-wide</span>
                      ) : (
                        <Button size="sm" variant="ghost" onClick={() => unblock.mutate(entry.id)}>
                          Unblock
                        </Button>
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Sheet>

        <Sheet className="settle settle-2 overflow-hidden">
          <SheetHeader
            title="Velocity limits"
            description="Cap how many payments, or how much money, one payer may push through in a window."
            actions={<Button onClick={() => setAddingRule(true)}>Add a limit</Button>}
          />
          {rules.isLoading ? (
            <Skeleton rows={3} />
          ) : rules.error ? (
            <ErrorNotice error={rules.error} className="m-5" />
          ) : ruleRows.length === 0 ? (
            <EmptyState
              title="No limits set"
              description="Start with a watching limit: it records what it would have stopped without refusing anyone."
              action={<Button onClick={() => setAddingRule(true)}>Add a limit</Button>}
            />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Limit</Th>
                  <Th>When it fires</Th>
                  <Th>State</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {ruleRows.map((rule) => (
                  <Tr key={rule.id}>
                    <Td className="max-w-md">{rule.description}</Td>
                    <Td>
                      <StatusPill status={rule.action} />
                      <span className="ml-2 text-[12.5px] text-ink-muted">
                        {rule.action === "block" ? "Refuses the payment" : "Records it only"}
                      </span>
                    </Td>
                    <Td className="text-ink-muted">{rule.enabled ? "On" : "Off"}</Td>
                    <Td align="right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => toggleRule.mutate({ id: rule.id, enabled: !rule.enabled })}
                      >
                        {rule.enabled ? "Turn off" : "Turn on"}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteRule.mutate(rule.id)}>
                        Delete
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Sheet>

        <Sheet className="settle settle-3 overflow-hidden">
          <SheetHeader
            title="Recent decisions"
            description="Every time a control fired, and why. This is the answer when a customer asks why they were refused."
          />
          {decisions.isLoading ? (
            <Skeleton rows={4} />
          ) : decisions.error ? (
            <ErrorNotice error={decisions.error} className="m-5" />
          ) : decisionRows.length === 0 ? (
            <EmptyState
              title="Nothing has been stopped"
              description="Decisions appear here whenever a blocklist entry or a limit matches."
            />
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>When</Th>
                  <Th>Outcome</Th>
                  <Th>Reason</Th>
                  <Th>Payment</Th>
                </tr>
              </thead>
              <tbody>
                {decisionRows.map((decision) => (
                  <Tr key={decision.id}>
                    <Td className="whitespace-nowrap text-ink-muted">
                      {formatDateTime(decision.created_at)}
                    </Td>
                    <Td>
                      <StatusPill status={decision.action} />
                    </Td>
                    <Td className="max-w-md">{decision.reason}</Td>
                    <Td>
                      {decision.payment_id ? (
                        <a
                          href={`/payments/${decision.payment_id}`}
                          className="font-mono text-[12px] text-verdigris underline-offset-2 hover:underline"
                        >
                          {decision.payment_id.slice(0, 12)}…
                        </a>
                      ) : (
                        <span className="text-[12px] text-ink-faint">Refused before creation</span>
                      )}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Sheet>
      </div>

      <BlockDialog
        open={blocking}
        onClose={() => setBlocking(false)}
        onSaved={() => {
          setBlocking(false);
          queryClient.invalidateQueries({ queryKey: ["blocklist"] });
        }}
      />

      <RuleDialog
        open={addingRule}
        onClose={() => setAddingRule(false)}
        onSaved={() => {
          setAddingRule(false);
          queryClient.invalidateQueries({ queryKey: ["velocity-rules"] });
        }}
      />
    </>
  );
}

function BlockDialog({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [kind, setKind] = useState("mobile");
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");

  const block = useMutation({
    mutationFn: () => api.post<BlocklistEntry>("/api/dashboard/fraud/blocklist", { kind, value, reason }),
    onSuccess: onSaved,
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Block a value"
      description="Anything matching this is refused before a payment is created, so no money moves and nothing reaches the gateway."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="danger" loading={block.isPending} onClick={() => block.mutate()}>
            Block
          </Button>
        </>
      }
    >
      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          block.mutate();
        }}
      >
        <ErrorNotice error={block.error} />
        <fieldset className="grid gap-1.5">
          <legend className="label mb-1">What are you blocking?</legend>
          {Object.entries(KIND_LABELS).map(([key, label]) => (
            <label key={key} className="flex cursor-pointer items-center gap-2.5 py-0.5">
              <input
                type="radio"
                name="kind"
                value={key}
                checked={kind === key}
                onChange={() => setKind(key)}
                className="h-3.5 w-3.5 accent-verdigris"
              />
              <span className="text-[13px] text-ink">{label}</span>
            </label>
          ))}
        </fieldset>
        <Field
          label="Value"
          required
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={kind === "mobile" ? "0771 234 567" : "someone@example.com"}
          hint="We match it in the same form the payment path stores, so a number typed either way still matches."
        />
        <Field
          label="Why?"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Disputed three payments in one week"
          hint="Only you see this. Your customer is told the payment could not be accepted, and nothing more."
        />
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}

function RuleDialog({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    scope: "payer_mobile",
    window: "60",
    maxPayments: "5",
    maxAmount: "",
    currency: "USD",
    enforce: false,
  });

  const add = useMutation({
    mutationFn: () =>
      api.post<VelocityRule>("/api/dashboard/fraud/velocity-rules", {
        scope: form.scope,
        window_minutes: Number(form.window || 60),
        max_payments: Number(form.maxPayments || 0),
        max_amount_minor: toMinor(form.maxAmount) ?? 0,
        currency: form.currency,
        action: form.enforce ? "block" : "flag",
      }),
    onSuccess: onSaved,
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add a velocity limit"
      description="Set a payment count, an amount, or both. A limit that caps neither does nothing and will be refused."
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={add.isPending} onClick={() => add.mutate()}>
            Add limit
          </Button>
        </>
      }
    >
      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          add.mutate();
        }}
      >
        <ErrorNotice error={add.error} />

        <fieldset className="grid gap-1.5">
          <legend className="label mb-1">Count it per…</legend>
          {[
            { value: "payer_mobile", label: "Mobile number", hint: "One payer's handset." },
            { value: "payer_email", label: "Email address", hint: "One payer's address." },
            { value: "tenant", label: "Your whole account", hint: "All traffic, whoever it comes from." },
          ].map((option) => (
            <label key={option.value} className="flex cursor-pointer items-start gap-2.5 py-0.5">
              <input
                type="radio"
                name="scope"
                value={option.value}
                checked={form.scope === option.value}
                onChange={() => setForm({ ...form, scope: option.value })}
                className="mt-0.5 h-3.5 w-3.5 accent-verdigris"
              />
              <span>
                <span className="block text-[13px] text-ink">{option.label}</span>
                <span className="block text-[12px] text-ink-muted">{option.hint}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <Field
          label="Over how many minutes?"
          inputMode="numeric"
          value={form.window}
          onChange={(e) => setForm({ ...form, window: e.target.value })}
          hint="60 is an hour, 1440 a day. The longest allowed is 10080, a week."
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Most payments"
            inputMode="numeric"
            value={form.maxPayments}
            onChange={(e) => setForm({ ...form, maxPayments: e.target.value })}
            hint="0 for no count limit."
          />
          <Field
            label={`Most ${form.currency}`}
            inputMode="decimal"
            value={form.maxAmount}
            onChange={(e) => setForm({ ...form, maxAmount: e.target.value })}
            placeholder="500.00"
            hint="Leave blank for no amount limit."
          />
        </div>

        <Checkbox
          label="Refuse payments that break this limit"
          hint="Leave this off to start: the limit records what it would have stopped without refusing anyone."
          checked={form.enforce}
          onChange={(enforce) => setForm({ ...form, enforce })}
        />

        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
